import {CLICK_SOUNDS, ClickSoundId, SoundSection, clickSoundName} from "./sounds";

interface SettingsSheetOptions {
    /** The current selection per section, echoed in the tab labels. */
    getSelection: (section: SoundSection) => ClickSoundId;
    /**
     * Called when the user picks a sound row. The caller persists the choice,
     * swaps the live click sound, and previews it.
     */
    onSelect: (section: SoundSection, sound: ClickSoundId) => void;
}

/**
 * The Settings bottom sheet: slides up from the bottom of the screen, with
 * segmented Downbeat/Secondary tabs that each list the full sound catalog as
 * big touch rows (native radio inputs). One list is visible at a time, and
 * each tab echoes its current choice.
 *
 * Standard modal behaviour: focus trap, Escape and backdrop click close, and
 * focus is restored to the gear button on close.
 *
 * The markup lives in index.html; this class wires it up and renders the
 * sound rows from the catalog.
 */
class SettingsSheet {

    private readonly options: SettingsSheetOptions;
    private readonly backdrop: HTMLElement;
    private readonly sheet: HTMLElement;
    private readonly panel: HTMLElement;
    private readonly openButton: HTMLElement;
    private readonly soundList: HTMLElement;
    private readonly tabs: HTMLElement[];

    private activeSection: SoundSection = 'downbeat';
    private isOpen = false;
    private lastFocused: HTMLElement | null = null;

    constructor(options: SettingsSheetOptions) {
        this.options = options;
        this.backdrop = document.getElementById('settings-backdrop')!;
        this.sheet = document.getElementById('settings-sheet')!;
        this.panel = document.getElementById('settings-panel')!;
        this.openButton = document.getElementById('settings-open')!;
        this.soundList = document.getElementById('settings-sounds')!;
        this.tabs = Array.from(this.backdrop.querySelectorAll<HTMLElement>('.settings-tab'));

        this.openButton.addEventListener('click', () => this.open());
        this.backdrop.querySelector('.sheet-close')!.addEventListener('click', () => this.close());
        this.backdrop.addEventListener('click', (event) => {
            if (event.target === this.backdrop) {
                this.close();
            }
        });
        this.backdrop.addEventListener('keydown', (event) => this.onKeydown(event));
        this.tabs.forEach(tab => {
            tab.addEventListener('click', () => this.selectSection(tab.dataset.section as SoundSection));
        });

        this.selectSection('downbeat');
        this.syncEchoes();
    }

    open() {
        if (this.isOpen) {
            return;
        }
        this.isOpen = true;
        this.lastFocused = document.activeElement as HTMLElement;

        // always reopen on the Downbeat tab
        this.selectSection('downbeat');

        this.backdrop.hidden = false;
        // add the class on the next frame so the slide-up transition runs
        requestAnimationFrame(() => this.backdrop.classList.add('open'));
        document.body.classList.add('sheet-open');
        this.sheet.focus();
    }

    private close() {
        if (!this.isOpen) {
            return;
        }
        this.isOpen = false;

        this.backdrop.classList.remove('open');
        document.body.classList.remove('sheet-open');
        const hide = () => {
            if (!this.isOpen) {
                this.backdrop.hidden = true;
            }
        };
        this.sheet.addEventListener('transitionend', hide, {once: true});
        // fallback in case the transition is interrupted and never ends
        setTimeout(hide, 300);
        (this.lastFocused ?? this.openButton).focus();
    }

    private onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            event.stopPropagation();
            this.close();
            return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            // tablist arrow-key navigation, with automatic activation
            const target = event.target as HTMLElement;
            if (target.classList.contains('settings-tab')) {
                event.preventDefault();
                const index = this.tabs.indexOf(target);
                const direction = event.key === 'ArrowLeft' ? -1 : 1;
                const next = this.tabs[(index + direction + this.tabs.length) % this.tabs.length];
                this.selectSection(next.dataset.section as SoundSection);
                next.focus();
            }
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        // focus trap
        const focusables = Array.from(
            this.backdrop.querySelectorAll<HTMLElement>('button, input, [href], [tabindex]')
        ).filter(el => !el.hasAttribute('disabled') && el.tabIndex >= 0);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    /**
     * Switch the visible sound list to the given section, keeping the tab
     * states (aria-selected, roving tabindex) and the panel label in sync.
     */
    private selectSection(section: SoundSection) {
        this.activeSection = section;
        this.tabs.forEach(tab => {
            const isActive = tab.dataset.section === section;
            tab.classList.toggle('active', isActive);
            tab.setAttribute('aria-selected', '' + isActive);
            tab.tabIndex = isActive ? 0 : -1;
        });
        this.panel.setAttribute('aria-labelledby', `settings-tab-${section}`);
        this.soundList.setAttribute('aria-label', `${section === 'downbeat' ? 'Downbeat' : 'Secondary'} sound`);
        this.renderRows();
    }

    private renderRows() {
        const selection = this.options.getSelection(this.activeSection);
        this.soundList.innerHTML = '';
        for (const sound of CLICK_SOUNDS) {
            const row = document.createElement('label');
            row.className = 'sound-row';
            row.innerHTML = `
                <input type="radio" name="${this.activeSection}-sound" value="${sound.id}">
                <span class="sound-radio" aria-hidden="true"></span>
                <span class="sound-name">${sound.name}</span>
                <svg class="sound-check" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"/></svg>`;
            const input = row.querySelector('input')!;
            input.checked = sound.id === selection;
            input.addEventListener('change', () => this.selectSound(sound.id));
            this.soundList.appendChild(row);
        }
    }

    private selectSound(sound: ClickSoundId) {
        this.options.onSelect(this.activeSection, sound);
        this.echo(this.activeSection).textContent = clickSoundName(sound);
    }

    /** Refresh both tabs' current-choice echoes from the current selections. */
    private syncEchoes() {
        this.tabs.forEach(tab => {
            const section = tab.dataset.section as SoundSection;
            this.echo(section).textContent = clickSoundName(this.options.getSelection(section));
        });
    }

    private echo(section: SoundSection): HTMLElement {
        return this.backdrop.querySelector(`#settings-tab-${section} .settings-tab-choice`)!;
    }
}

export {SettingsSheet, SettingsSheetOptions};
